import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { authClient } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/invite/$invitationId")({
  component: InvitePage,
});

interface InvitationInfo {
  email: string;
  organizationName: string;
  userExists: boolean;
}

function InvitePage() {
  const { invitationId } = Route.useParams();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "loading" | "accepting" | "success" | "error"
  >("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const acceptAttempted = useRef(false);
  const [inviteInfo, setInviteInfo] = useState<InvitationInfo | null>(null);
  const [inviteInfoError, setInviteInfoError] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || "";
  const isLoggedIn = !!session?.user;

  // Fetch invitation info (public, no auth needed)
  useEffect(() => {
    const url = apiBase
      ? `${apiBase}/api/invitations/${invitationId}/info`
      : `/api/invitations/${invitationId}/info`;

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setInviteInfoError(
            (body as any)?.error ?? "Invitation not found",
          );
          return;
        }
        const data = (await res.json()) as InvitationInfo;
        setInviteInfo(data);
      })
      .catch(() => {
        setInviteInfoError("Failed to load invitation details");
      });
  }, [apiBase, invitationId]);

  // Auto-accept when logged in
  useEffect(() => {
    if (sessionPending) return;
    if (!isLoggedIn) return;
    if (acceptAttempted.current) return;
    acceptAttempted.current = true;

    setStatus("accepting");

    authClient.organization
      .acceptInvitation({ invitationId })
      .then(async ({ data, error }) => {
        if (error) {
          setStatus("error");
          setErrorMsg(error.message ?? "Failed to accept invitation");
          return;
        }

        // Set the new org as active (best-effort — invite is already accepted)
        try {
          if (data?.member?.organizationId) {
            await authClient.organization.setActive({
              organizationId: data.member.organizationId,
            });
          }
        } catch {
          // Proceed to success even if setActive fails
        }

        setStatus("success");
        setTimeout(() => navigate({ to: "/" }), 1000);
      })
      .catch(() => {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again.");
      });
  }, [sessionPending, isLoggedIn, invitationId, navigate]);

  // Redirect to login with proper mode/email once we know invite info
  useEffect(() => {
    if (sessionPending) return;
    if (isLoggedIn) return;
    if (!inviteInfo) return;

    navigate({
      to: "/login",
      search: {
        redirect: `/invite/${invitationId}`,
        mode: inviteInfo.userExists ? undefined : "signup",
        email: inviteInfo.email,
      },
    });
  }, [sessionPending, isLoggedIn, inviteInfo, invitationId, navigate]);

  // Loading state
  if (sessionPending || (!isLoggedIn && !inviteInfo && !inviteInfoError)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Loader2 className="size-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  // Invitation fetch error (expired, not found, etc.)
  if (inviteInfoError && !isLoggedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(39,39,42,0.3)_0%,transparent_70%)]" />
        <div className="relative w-full max-w-sm">
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 p-8 backdrop-blur-sm text-center">
            <div className="mb-4 flex justify-center">
              <div className="size-12">
                <img src="/cord-logo.svg" alt="Cord" className="size-full rounded-xl" />
              </div>
            </div>
            <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-red-900/30">
              <svg className="size-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-zinc-100">Invalid invitation</h2>
            <p className="mt-1.5 text-sm text-zinc-500">{inviteInfoError}</p>
            <Link
              to="/login"
              search={{ redirect: undefined, mode: undefined, email: undefined }}
              className="mt-4 inline-flex rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-white"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Logged in — show accept status
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(39,39,42,0.3)_0%,transparent_70%)]" />

      <div className="relative w-full max-w-sm animate-[slide-in-up_0.3s_ease-out]">
        <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 p-8 backdrop-blur-sm text-center">
          <div className="mb-4 flex justify-center">
            <div className="size-12">
              <img src="/cord-logo.svg" alt="Cord" className="size-full rounded-xl" />
            </div>
          </div>

          {status === "accepting" && (
            <>
              <Loader2 className="mx-auto mb-3 size-6 animate-spin text-zinc-400" />
              <h2 className="text-lg font-semibold text-zinc-100">
                Accepting invitation...
              </h2>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-emerald-900/30">
                <svg className="size-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-zinc-100">
                Welcome to the team!
              </h2>
              <p className="mt-1.5 text-sm text-zinc-500">
                Redirecting you now...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-red-900/30">
                <svg className="size-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-zinc-100">
                Couldn't accept invitation
              </h2>
              <p className="mt-1.5 text-sm text-zinc-500">{errorMsg}</p>
              <Link
                to="/"
                className="mt-4 inline-flex rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-white"
              >
                Go to dashboard
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
